import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, LogOut, Package, Mail, ShoppingBag, FileText, Tag, Upload, Loader2, Ticket, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { productImage } from "@/lib/images";

const EMPTY_PRODUCT = {
  name: "", category: "Bears", description: "", price: 0,
  image_url: "", gallery: [], in_stock: true, allow_personalization: true, featured: false,
  size: "",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");

  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [codes, setCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);

  const [editing, setEditing] = useState(null); // product object or {} for new
  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [uploading, setUploading] = useState(false);

  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image is too large (max 15 MB).");
      return;
    }
    setUploading(true);
    const t = toast.loading("Cleaning background with AI… this can take ~20s");
    try {
      const body = new FormData();
      body.append("file", file);
      const params = new URLSearchParams({ clean: "true" });
      if (formData.name) params.set("name", formData.name);
      const res = await api.post(`/admin/upload-product-image?${params.toString()}`, body, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setFormData((f) => ({ ...f, image_url: res.data.image_url }));
      if (res.data.cleaned === false && res.data.warning) {
        toast.warning(res.data.warning, { id: t });
      } else {
        toast.success("Photo uploaded and cleaned ✓", { id: t });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed", { id: t });
    } finally {
      setUploading(false);
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [p, i, o, c] = await Promise.all([
        api.get("/products"),
        api.get("/inquiries"),
        api.get("/orders"),
        api.get("/admin/discount-codes"),
      ]);
      setProducts(p.data); setInquiries(i.data); setOrders(o.data); setCodes(c.data);
    } catch (err) {
      console.error("admin: loadAll failed", err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success(`Copied ${code} to clipboard`);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1800);
    } catch (err) {
      console.warn("clipboard write failed", err);
      toast.error("Couldn't copy — long-press to select");
    }
  };

  const onLogout = async () => { await logout(); nav("/"); };

  const openNew = () => { setFormData(EMPTY_PRODUCT); setEditing({}); };
  const openEdit = (p) => {
    setFormData({
      name: p.name, category: p.category, description: p.description,
      price: p.price, image_url: p.image_url, gallery: p.gallery || [],
      in_stock: p.in_stock, allow_personalization: p.allow_personalization, featured: p.featured,
      size: p.size || "",
    });
    setEditing(p);
  };
  const closeForm = () => { setEditing(null); setFormData(EMPTY_PRODUCT); };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, size: formData.size || null };
      if (editing?.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved");
      closeForm(); loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); loadAll(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="container-x py-12 lg:py-16" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-6">
        <div>
          <div className="font-script text-2xl text-[#D97757]">studio</div>
          <h1 className="font-serif text-4xl mt-1">Welcome back, {user?.name?.split(" ")[0] || "Admin"}</h1>
        </div>
        <button onClick={onLogout} data-testid="admin-logout-btn" className="btn-outline inline-flex items-center gap-2">
          <LogOut size={16} /> Sign out
        </button>
      </div>

      <div className="mt-8 flex gap-2 border-b border-[#EAE3D9]">
        {[
          ["products", "Products", Package, products.length],
          ["inquiries", "Inquiries", Mail, inquiries.length],
          ["orders", "Orders", ShoppingBag, orders.length],
          ["codes", "Codes", Ticket, codes.length],
        ].map(([k, label, Icon, n]) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
              tab === k ? "border-[#D97757] text-[#D97757]" : "border-transparent text-[#5C4D43] hover:text-[#2C211B]"
            }`}
          >
            <Icon size={16} /> {label} <span className="text-xs bg-[#F4EFE6] rounded-full px-2 py-0.5">{n}</span>
          </button>
        ))}
      </div>

      {/* PRODUCTS TAB */}
      {tab === "products" && (
        <div className="mt-8" data-testid="products-tab">
          <div className="flex justify-end gap-2 flex-wrap">
            <a
              data-testid="download-tags-btn"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/swingtag?pdf=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline inline-flex items-center gap-2"
              title="Download A4 sheet of swing tags as a PDF"
            >
              <Tag size={16} /> Download swing tags PDF
            </a>
            <a
              data-testid="view-tags-btn"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/swingtag`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline inline-flex items-center gap-2"
            >
              View tags
            </a>
            <button onClick={openNew} data-testid="new-product-btn" className="btn-primary inline-flex items-center gap-2">
              <Plus size={16} /> New product
            </button>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="card-soft p-4 flex gap-3" data-testid={`admin-product-${p.id}`}>
                <img src={productImage(p.image_url)} alt={p.name} className="w-20 h-20 rounded-2xl object-cover bg-[#F4EFE6]" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg truncate">{p.name}</div>
                  <div className="text-xs text-[#5C4D43]">{p.category} · £{p.price.toFixed(2)}</div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D9] inline-flex items-center gap-1">
                      <Edit2 size={12} /> Edit
                    </button>
                    <a
                      data-testid={`doc-${p.id}`}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/doc/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-3 py-1.5 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D9] inline-flex items-center gap-1"
                      title="Open Declaration of Conformity (print to PDF)"
                    >
                      <FileText size={12} /> DoC
                    </a>
                    <button data-testid={`delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D9] inline-flex items-center gap-1 text-[#D97757]">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* INQUIRIES TAB */}
      {tab === "inquiries" && (
        <div className="mt-8 space-y-3" data-testid="inquiries-tab">
          {inquiries.length === 0 && <div className="text-[#5C4D43] py-12 text-center">No inquiries yet.</div>}
          {inquiries.map((i) => (
            <div key={i.id} className="card-soft p-5" data-testid={`inquiry-${i.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-serif text-lg">{i.name} · <span className="text-[#5C4D43]">{i.animal_type}</span></div>
                  <div className="text-sm text-[#5C4D43]">{i.email}{i.phone ? ` · ${i.phone}` : ""}</div>
                </div>
                <span className="text-xs uppercase tracking-widest text-[#D97757]">{i.status}</span>
              </div>
              {(i.colour || i.child_name || i.birthday) && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {i.colour && <span className="px-3 py-1 rounded-full bg-[#F4EFE6]"><b>Colour:</b> {i.colour}</span>}
                  {i.child_name && <span className="px-3 py-1 rounded-full bg-[#F4EFE6]"><b>Name on tag:</b> {i.child_name}</span>}
                  {i.birthday && <span className="px-3 py-1 rounded-full bg-[#F4EFE6]"><b>Birthday:</b> {i.birthday}</span>}
                </div>
              )}
              <p className="mt-3 text-[#2C211B] whitespace-pre-line">{i.description}</p>
              <div className="mt-3 text-xs text-[#5C4D43]">
                {i.budget && <>Budget: {i.budget} · </>}
                {i.deadline && <>Needed by: {i.deadline}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ORDERS TAB */}
      {tab === "orders" && (
        <div className="mt-8 space-y-3" data-testid="orders-tab">
          {orders.length === 0 && <div className="text-[#5C4D43] py-12 text-center">No orders yet.</div>}
          {orders.map((o) => (
            <div key={o.id} className="card-soft p-5" data-testid={`order-${o.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-serif text-xl">{o.customer?.customer_name || "—"}</div>
                  <div className="text-sm text-[#5C4D43]">
                    <a href={`mailto:${o.customer?.customer_email || ""}`} className="underline">
                      {o.customer?.customer_email}
                    </a>
                  </div>
                  {o.customer?.discount_code && (
                    <div className="mt-1 inline-block text-[10px] uppercase tracking-widest bg-[#F4EFE6] px-2 py-0.5 rounded-full">
                      code: {o.customer.discount_code}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-serif text-2xl text-[#D97757]">£{o.amount.toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A3B18A] mt-0.5">PAID</div>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-xl bg-[#F4EFE6]/70 border border-[#EAE3D9]" data-testid={`order-address-${o.id}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C4D43] mb-1">Ship to</div>
                <div className="text-sm text-[#2C211B] whitespace-pre-line leading-relaxed">
                  {o.customer?.shipping_address || "—"}
                </div>
              </div>

              <div className="mt-4 space-y-1 text-sm">
                {o.items?.map((it, idx) => (
                  <div key={idx} className="flex justify-between gap-3">
                    <div className="min-w-0">
                      {it.quantity}× {it.name}
                      {it.personalization && Object.keys(it.personalization).length > 0 && (
                        <span className="block text-xs text-[#5C4D43] ml-5">
                          {Object.entries(it.personalization).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                        </span>
                      )}
                    </div>
                    <div className="text-[#5C4D43] whitespace-nowrap">£{(it.price * it.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CODES TAB — private to admin only */}
      {tab === "codes" && (
        <div className="mt-8 space-y-3" data-testid="codes-tab">
          <div className="max-w-2xl">
            <p className="text-sm text-[#5C4D43] leading-relaxed">
              These codes are <b>not shown to customers</b>. Tap a card to copy the code to your clipboard, then paste it into a message, email or Insta DM whenever you want to offer a discount.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {codes.length === 0 && (
              <div className="text-[#5C4D43] py-10 text-center col-span-full">No codes configured.</div>
            )}
            {codes.map((c) => (
              <button
                key={c.code}
                onClick={() => copyCode(c.code)}
                data-testid={`admin-code-${c.code}`}
                className="text-left card-soft p-5 hover:border-[#D97757] transition-all flex items-start justify-between gap-4 group"
                title={`Copy ${c.code}`}
              >
                <div className="min-w-0">
                  <div className="font-mono text-lg tracking-wider text-[#2C211B] select-all">{c.code}</div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#A3B18A] mt-1">{c.summary}</div>
                </div>
                <div
                  data-testid={`copy-${c.code}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${
                    copiedCode === c.code
                      ? "bg-[#A3B18A]/20 text-[#5C7B58]"
                      : "bg-[#F4EFE6] text-[#5C4D43] group-hover:bg-[#D97757] group-hover:text-white"
                  }`}
                >
                  {copiedCode === c.code ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PRODUCT FORM MODAL */}
      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeForm} data-testid="product-form-modal">
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveProduct}
            className="bg-white rounded-3xl p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto"
          >
            <div className="font-serif text-2xl mb-4">{editing?.id ? "Edit product" : "New product"}</div>
            <div className="space-y-3">
              <input data-testid="pf-name" required className="input-warm" placeholder="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <div className="grid grid-cols-3 gap-3">
                <input data-testid="pf-category" required className="input-warm" placeholder="Category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
                <select
                  data-testid="pf-size"
                  className="input-warm"
                  value={formData.size || ""}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                >
                  <option value="">Size (none)</option>
                  <option value="Small">Small</option>
                  <option value="Medium">Medium</option>
                  <option value="Large">Large</option>
                </select>
                <input data-testid="pf-price" required type="number" step="0.01" className="input-warm" placeholder="Price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} />
              </div>
              <input data-testid="pf-image" required className="input-warm" placeholder="Image URL (or upload below)" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
              <div className="flex items-center gap-3 flex-wrap">
                <label
                  data-testid="pf-upload-btn"
                  className={`btn-outline inline-flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                  title="Upload a phone photo — we'll drop it onto a clean cream studio backdrop automatically"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? "Cleaning…" : "Upload from your phone"}
                  <input
                    data-testid="pf-upload-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={onUploadPhoto}
                    disabled={uploading}
                  />
                </label>
                {formData.image_url && (
                  <img
                    data-testid="pf-image-preview"
                    src={productImage(formData.image_url)}
                    alt="preview"
                    className="w-14 h-14 rounded-xl object-cover bg-[#F4EFE6] border border-[#EAE3D9]"
                  />
                )}
              </div>
              <textarea data-testid="pf-description" required className="input-warm min-h-[100px]" placeholder="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              <div className="flex flex-wrap gap-4 text-sm text-[#5C4D43]">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={formData.in_stock} onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })} /> In stock</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} /> Featured</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={formData.allow_personalization} onChange={(e) => setFormData({ ...formData, allow_personalization: e.target.checked })} /> Personalize</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closeForm} className="btn-outline">Cancel</button>
              <button data-testid="pf-save" type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
